import { dehyphenateString } from '~~/utils';

function getLabelForNoCityAndNoRegionType(state: string) {
	return `em ${state}`;
}

function getLabelForNoRegion(zoneOrCity: string, state: string): string {
	return zoneOrCity
		? `em ${zoneOrCity} - ${state}`
		: getLabelForNoCityAndNoRegionType(state);
}

function getLabelForOneRegion(
	region: string,
	zoneOrCity: string,
	state: string
): string {
	return `em ${region}, ${zoneOrCity} - ${state}`;
}

function getLabelForMoreThanOneRegion(
	region: string,
	numberOfAditionalRegions: number,
	zoneOrCity: string,
	state: string
): string {
	return `em ${region} e mais ${numberOfAditionalRegions}, ${zoneOrCity} - ${state}`;
}

function getLabelForAddressSlug(addressSlug: string): string {
	return `em ${addressSlug} e arredores`;
}

type ListingInfo = {
	regions: string[];
	cities: string[];
	regionType?: string | null;
	state: string;
	addressSlug?: string | null;
};

type ListingInfoGetter = () => ListingInfo;

export function useListingInfoLabel(infoGetter: ListingInfoGetter) {
	const info: ComputedRef<ListingInfo> = computed<ListingInfo>(() =>
		infoGetter()
	);

	const numberOfRegions = computed<number>(() => info.value.regions.length);
	const numberOfAditionalRegions = computed<number>(
		() => numberOfRegions.value - 1
	);
	const hasMoreThanOneRegion = computed<boolean>(
		() => numberOfRegions.value > 1
	);

	const infoLabel = computed<string | null>(() => {
		if (info.value.addressSlug) {
			return getLabelForAddressSlug(info.value.addressSlug);
		}

		const state = info.value.state;
		const region = info.value.regions[0] as string;

		const cityOrRegionType =
			info.value.cities[0] || info.value.regionType || '';

		if (!cityOrRegionType) {
			return getLabelForNoCityAndNoRegionType(state);
		}

		if (!numberOfRegions.value) {
			return getLabelForNoRegion(cityOrRegionType, state);
		}

		if (hasMoreThanOneRegion.value) {
			return getLabelForMoreThanOneRegion(
				region,
				numberOfAditionalRegions.value,
				cityOrRegionType,
				state
			);
		}

		if (!hasMoreThanOneRegion.value) {
			return getLabelForOneRegion(region, cityOrRegionType, state);
		}

		return null;
	});

	return infoLabel;
}
